import { SECRET_KEYS, SECRET_PATHS, setCache } from './secrets'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ResolvedSecrets {
  [key: string]: string
}

// ---------------------------------------------------------------------------
// GCP Metadata Server helpers
// ---------------------------------------------------------------------------

const METADATA_BASE = 'http://metadata.google.internal'
const GSM_BASE = 'https://secretmanager.googleapis.com/v1'

/**
 * Obtain a short-lived OAuth2 access token from the GCP metadata server.
 * The token is automatically scoped to the service account attached to the
 * Compute Engine / Cloud Run / GKE instance.
 */
async function fetchAccessToken(): Promise<string> {
  const response = await fetch(
    `${METADATA_BASE}/computeMetadata/v1/instance/service-accounts/default/token`,
    {
      headers: { 'Metadata-Flavor': 'Google' },
    }
  )
  if (!response.ok) {
    throw new Error(
      `GCP metadata server token request failed: ${response.status} ${response.statusText}`
    )
  }
  const body = (await response.json()) as {
    access_token: string
    expires_in: number
    token_type: string
  }
  return body.access_token
}

/**
 * Fetch the UTF-8 payload of the latest version of a single secret via the
 * Secret Manager REST API {@link https://cloud.google.com/secret-manager/docs/reference/rest/v1/projects.secrets.versions/access}.
 */
async function fetchSecretValue(path: string, accessToken: string): Promise<string> {
  const response = await fetch(`${GSM_BASE}/${path}:access`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!response.ok) {
    throw new Error(
      `Secret Manager access failed for "${path}": ${response.status} ${response.statusText}`
    )
  }
  const body = (await response.json()) as {
    payload?: { data?: string }
  }
  const encoded = body.payload?.data
  if (!encoded) {
    throw new Error(`Secret Manager returned an empty payload for "${path}"`)
  }
  return atob(encoded) // base64 → UTF-8
}

// ---------------------------------------------------------------------------
// Local / fallback helpers
// ---------------------------------------------------------------------------

/**
 * Build the cache from VITE_* env vars. This is the standard Vite convention
 * for passing build-time variables into the app:
 *
 *   VITE_STRIPE_PUBLISHABLE_KEY → import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
 *   VITE_COT_ACCOUNT_ID        → import.meta.env.VITE_COT_ACCOUNT_ID
 *   … etc.
 *
 * @returns `true` when every known secret key has a non-empty value.
 */
const PROD_SECRET_TO_VITE_ENV_VAR: Record<string, string> = {
  GOOGLE_GEOCODE_API_KEY: 'VITE_GOOGLE_GEOCODE_API_KEY',
  STRIPE_PUBLISHABLE_KEY: 'VITE_STRIPE_PUBLISHABLE_KEY',
  STRIPE_CLIENT_ID: 'VITE_STRIPE_CLIENT_ID',
  COT_ACCOUNT_ID: 'VITE_COT_ACCOUNT_ID',
  VM_API_KEY: 'VITE_VM_API_KEY',
  VM_API_URL: 'VITE_VM_API_URL',
}

const DEV_SECRET_TO_VITE_ENV_VAR: Record<string, string> = {
  GOOGLE_GEOCODE_API_KEY: 'VITE_GOOGLE_GEOCODE_API_KEY',
  STRIPE_PUBLISHABLE_KEY: 'VITE_DEV_STRIPE_PUBLISHABLE_KEY',
  STRIPE_CLIENT_ID: 'VITE_DEV_STRIPE_CLIENT_ID',
  COT_ACCOUNT_ID: 'VITE_DEV_COT_ACCOUNT_ID',
  VM_API_KEY: 'VITE_DEV_VM_API_KEY',
  VM_API_URL: 'VITE_DEV_VM_API_URL',
}

function tryFromEnvVars(): Record<string, string> | null {
  const resolved: Record<string, string> = {}
  const secretToViteEnvVar = import.meta.env.PROD
    ? PROD_SECRET_TO_VITE_ENV_VAR
    : DEV_SECRET_TO_VITE_ENV_VAR

  for (const key of SECRET_KEYS) {
    const varName = secretToViteEnvVar[key]
    const value = import.meta.env[varName] as string | undefined
    if (!value) return null // one missing → skip env-var fallback
    resolved[key] = value
  }
  return resolved
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve all secrets and cache them in memory.
 *
 * **GCP (production / staging):** fetches a short-lived access token from the
 * GCP metadata server, then reads every secret via the Secret Manager REST API.
 *
 * **Localhost (dev):** reads secrets from VITE_* environment variables so that
 * developers can provide them in a local `.env` file (or `.env.local`) without
 * needing to reach the GCP metadata server.
 *
 * Must be called once at application startup.
 *
 * @returns A `ResolvedSecrets` map of every key defined in `SECRET_KEYS`.
 */
export async function initializeSecrets(): Promise<ResolvedSecrets> {
  // ── Local dev: try VITE_* env vars first ──────────────────────────
  const fromEnv = tryFromEnvVars()
  if (fromEnv) {
    setCache(fromEnv)
    return fromEnv
  }

  // ── GCP: fetch from Secret Manager via metadata server ────────────
  const token = await fetchAccessToken()

  const entries = await Promise.all(
    SECRET_KEYS.map(async (key) => {
      const path = SECRET_PATHS[key]
      const value = await fetchSecretValue(path, token)
      return [key, value] as const
    })
  )

  const resolved = Object.fromEntries(entries) as ResolvedSecrets
  setCache(resolved)
  return resolved
}
